import dynamic from "next/dynamic";

import { useEffect, useRef, useState } from "react";

import { twMerge } from "tailwind-merge";

import useAuthentication from "hooks/useAuthentication";

import styles from "./style.module.scss";

const GoToTopButton = dynamic(() => import("components/GoToTopButton"));

export type PaneLayouProps = {
  className?: string;
  leftPaneClass?: string;
  top?: React.ReactNode;
  left?: React.ReactNode;
  hideLeft?: boolean;
  right?: React.ReactNode;
  hideRight?: boolean;
  center?: React.ReactNode;
  children?: React.ReactNode;
  bottom?: React.ReactNode;
  actionBar?: React.ReactNode;
  hideActionBar?: boolean;
  hideGoToTop?: boolean;
  topPaneClassName?: string;
  centerPaneClassName?: string;
  actionBarClassName?: string;
  middlePanesClassName?: string;
};

const PaneLayout = ({
  className = "",
  leftPaneClass = "",
  top = <></>,
  left = <></>,
  right = <></>,
  center,
  children,
  bottom = <></>,
  actionBar = <></>,
  hideActionBar,
  hideLeft,
  hideRight,
  hideGoToTop,
  topPaneClassName,
  centerPaneClassName,
  actionBarClassName,
  middlePanesClassName,
}: PaneLayouProps) => {
  const { authenticated } = useAuthentication();

  const centerContent = children || center || <></>;

  const topNavBarHeight = 60;
  const bottomMargin = 30;

  const [stickyTopRightPane, setStickyTopRightPane] = useState(topNavBarHeight);
  const [stickyTopActionBar, setStickyTopActionBar] = useState(topNavBarHeight);
  const [showGoToTop, setShowGoToTop] = useState(false);

  const rightPaneRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let updating = false;

    const updateSticky = () => {
      if (!updating) {
        updating = true;

        setTimeout(function () {
          const stickyTop =
            window.innerHeight -
            (rightPaneRef?.current?.clientHeight || 0) -
            bottomMargin;
          setStickyTopRightPane(
            stickyTop < topNavBarHeight ? stickyTop : topNavBarHeight
          );

          const stickyTopActionBar =
            window.innerHeight -
            (actionBarRef?.current?.clientHeight || 0) -
            bottomMargin;
          setStickyTopActionBar(
            stickyTopActionBar < topNavBarHeight
              ? stickyTopActionBar
              : topNavBarHeight
          );

          updating = false;
        }, 500);
      }
    };

    updateSticky();

    window.addEventListener("scroll", updateSticky, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateSticky);
    };
  }, [
    rightPaneRef?.current?.clientHeight,
    actionBarRef?.current?.clientHeight,
  ]);

  return (
    <>
      <div className={twMerge(styles.layoutContainer, className)}>
        <div className={styles.paneLayout}>
          <header className={topPaneClassName}>{top}</header>
          <div
            className={twMerge(
              styles.middlePanes,
              authenticated ? "" : "min-h-[calc(100vh-550px)]",
              middlePanesClassName
            )}
          >
            {!hideLeft && (
              <aside className={twMerge(styles.leftPane, leftPaneClass)}>
                {left}
              </aside>
            )}
            <main className={twMerge(styles.centerPane, centerPaneClassName)}>
              {centerContent}
            </main>
            {!hideActionBar && (
              <aside
                ref={actionBarRef}
                className={twMerge(styles.actionBar, actionBarClassName)}
                style={{ top: stickyTopActionBar }}
              >
                {actionBar}
              </aside>
            )}
            {!hideRight && (
              <aside
                className={twMerge(styles.rightPane, "md:hidden lg:block")}
                ref={rightPaneRef}
                style={{ top: stickyTopRightPane }}
              >
                {right}
              </aside>
            )}
          </div>
          <footer>{bottom}</footer>
        </div>
        {!hideGoToTop && showGoToTop && <GoToTopButton />}
      </div>
    </>
  );
};

export default PaneLayout;
